import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { pickSelected } from '../lib/dashboardPick.mjs'; // which row a saved id+event refers to (pure, unit-tested)

// The "choose a dashboard" control for goals, alerts and live updates.
//
// A client's catalogue is entity-wide, so a promoter with two events and a dozen
// report sets was picking from forty-odd flat rows — every one of them reading
// "<dashboard> · <set> · <event>". Naming the event made the rows tellable apart
// but did nothing about the length of the list. So the list is a TREE instead:
//
//   FF26                       ← tap an event
//     Cashless                 ← tap a category
//       Vendor Sales Overview  ← tap the dashboard
//
// Everything is shut until you open it, so you start at two or three events
// rather than forty dashboards. Levels that hold a single option are expanded
// for you — nobody should have to tap through a branch with no alternative — and
// the branch holding the current pick opens with the panel, so reopening the
// control shows you where you are.
//
// Typing searches every dashboard at once and flattens the tree to matches, each
// with its Event · Category path: the tree is for browsing, search for when you
// already know the name.
//
// Drop-in for the <select> it replaces: one dashboardId in, one out. Callers that
// need to know WHICH branch was tapped (share links pin a link to the event, so
// the same report under two events is two different picks) get the full
// catalogue row as a second argument — everyone else ignores it.
export default function DashboardPicker({
  value,
  onChange,
  dashboards = [],
  placeholder = 'Choose a dashboard…',
  isMobile = false,
  disabled = false,
  style,
  // The suite the CALLER is scoped to (an alert/goal belongs to one event). A
  // dashboard shared across events appears under every event branch with the
  // same id — without this, the trigger label named whichever branch came first
  // ("· LMC 2026" on a Shimza alert), reading as if the alert watched the wrong
  // event. The value resolves identically either way; only the label differed.
  preferSuiteId = '',
  // Label for a "none" row at the top of the panel. Only pickers whose choice is
  // OPTIONAL pass it (the campaign's extra-attributes source) — without a row to
  // tap, a source picked by mistake could never be taken off again.
  clearLabel = '',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [openEvents, setOpenEvents] = useState({});
  const [openSets, setOpenSets] = useState({});
  const btnRef = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Catalogue → events → categories → dashboards, keeping the server's order.
  // A dashboard attached to two events genuinely belongs under both branches;
  // it resolves to the same id either way, which the tree now makes visible
  // rather than hiding behind two identical-looking rows.
  const tree = useMemo(() => {
    const events = [];
    const byEvent = new Map();
    for (const d of dashboards) {
      const evName = d.suiteName || 'Other';
      const evKey = d.suiteId || evName;
      let ev = byEvent.get(evKey);
      if (!ev) { ev = { key: evKey, name: evName, sets: [], bySet: new Map() }; byEvent.set(evKey, ev); events.push(ev); }
      const setName = d.setName || 'Dashboards';
      let set = ev.bySet.get(setName);
      if (!set) { set = { key: `${evKey}::${setName}`, name: setName, items: [] }; ev.bySet.set(setName, set); ev.sets.push(set); }
      if (!set.items.some((x) => x.dashboardId === d.dashboardId)) set.items.push(d);
    }
    return events;
  }, [dashboards]);

  // Every dashboard as one flat row, for search and for the trigger's label.
  const flat = useMemo(() => tree.flatMap((ev) => ev.sets.flatMap((set) =>
    set.items.map((d) => ({ ...d, evName: ev.name, setName: set.name })))), [tree]);
  // Match the saved id, OR a dashboard FORKED from it: customising a dashboard
  // for a client swaps the id in their catalogue, and a picker that answers
  // "nothing selected" to a perfectly good saved source is how a working campaign
  // starts looking broken (2026-08-21). server/dashRef.js follows the same chain
  // when the source is actually read.
  const selected = pickSelected(flat, value, preferSuiteId);

  const ql = q.trim().toLowerCase();
  const matches = useMemo(() => (!ql ? [] : flat.filter((d) =>
    `${d.title} ${d.setName} ${d.evName}`.toLowerCase().includes(ql))), [ql, flat]);

  // Opening: anchor the panel, focus search, and reveal where the current pick
  // lives. A lone event or category counts as "already decided" and unfolds too.
  useEffect(() => {
    if (!open) return;
    const ev = {}; const sets = {};
    if (tree.length === 1) ev[tree[0].key] = true;
    // The caller's own event (the campaign's/alert's) opens with the panel — the
    // dashboards you're almost certainly after shouldn't need a tap to reveal.
    if (preferSuiteId && tree.some((e) => e.key === preferSuiteId)) ev[preferSuiteId] = true;
    for (const e of tree) if (e.sets.length === 1) sets[e.sets[0].key] = true;
    for (const e of tree) {
      for (const s of e.sets) {
        if (s.items.some((d) => d.dashboardId === value)) { ev[e.key] = true; sets[s.key] = true; }
      }
    }
    setOpenEvents(ev); setOpenSets(sets); setQ('');
    if (!isMobile) {
      const r = btnRef.current?.getBoundingClientRect();
      // Flip above the trigger when there isn't room below it.
      if (r) {
        const below = window.innerHeight - r.bottom;
        setPos(below > 300 || below > r.top
          ? { top: r.bottom + 4, left: r.left, width: r.width, maxHeight: Math.max(200, below - 16) }
          : { bottom: window.innerHeight - r.top + 4, left: r.left, width: r.width, maxHeight: Math.max(200, r.top - 16) });
      }
    }
    const t = setTimeout(() => inputRef.current?.focus(), 20);
    return () => clearTimeout(t);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const choose = (id, row) => { onChange(id, row); setOpen(false); };

  // A row can be listed but not choosable (e.g. a dashboard with nothing pickable
  // on it yet) — `disabledNote` says why instead of the row silently vanishing.
  const row = (d, showPath) => (
    <button
      key={`${d.suiteId || ''}:${d.setName}:${d.dashboardId}`}
      type="button"
      disabled={!!d.disabled}
      onClick={() => !d.disabled && choose(d.dashboardId, d)}
      style={{ ...leafBtn, background: d.dashboardId === value ? 'rgba(128,128,128,0.10)' : 'transparent', fontWeight: d.dashboardId === value ? 700 : 500, opacity: d.disabled ? 0.55 : 1, cursor: d.disabled ? 'not-allowed' : 'pointer' }}
    >
      <span style={ellipsis}>{d.title}</span>
      {d.disabledNote ? <span style={pathNote}>{d.disabledNote}</span> : showPath && <span style={pathNote}>{d.evName} · {d.setName}</span>}
    </button>
  );

  const panelBody = (
    <>
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search every dashboard…"
        aria-label="Search dashboards"
        style={searchInput}
      />
      <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: '4px 0' }}>
        {clearLabel && !ql && (
          <button type="button" onClick={() => choose('', null)}
            style={{ ...branchBtn, color: 'var(--muted)', fontWeight: value ? 500 : 700, borderBottom: '1px solid var(--hairline)' }}>
            <span style={ellipsis}>{clearLabel}</span>
          </button>
        )}
        {ql ? (
          matches.length ? matches.map((d) => row(d, true)) : <div style={muted}>No dashboard matches “{q}”</div>
        ) : tree.length === 0 ? (
          <div style={muted}>No dashboards available yet.</div>
        ) : tree.map((ev) => {
          const evOpen = !!openEvents[ev.key];
          return (
            <div key={ev.key}>
              <button type="button" onClick={() => setOpenEvents((o) => ({ ...o, [ev.key]: !evOpen }))} style={branchBtn}>
                <span style={{ ...caret, transform: evOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
                <span style={{ ...ellipsis, fontWeight: 800 }}>{ev.name}</span>
                <span style={countNote}>{ev.sets.reduce((n, s) => n + s.items.length, 0)}</span>
              </button>
              {evOpen && ev.sets.map((set) => {
                const catOpen = !!openSets[set.key]; // not `setOpen` — that's the panel's state setter
                return (
                  <div key={set.key}>
                    <button type="button" onClick={() => setOpenSets((o) => ({ ...o, [set.key]: !catOpen }))} style={{ ...branchBtn, paddingLeft: 26 }}>
                      <span style={{ ...caret, transform: catOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
                      <span style={{ ...ellipsis, fontWeight: 700, fontSize: 12.5 }}>{set.name}</span>
                      <span style={countNote}>{set.items.length}</span>
                    </button>
                    {catOpen && set.items.map((d) => row({ ...d, evName: ev.name, setName: set.name }, false))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{ ...trigger, ...style, color: selected ? 'var(--text)' : 'var(--muted)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
      >
        <span style={ellipsis}>
          {selected ? selected.title : placeholder}
          {selected && <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {selected.setName} · {selected.evName}</span>}
        </span>
        <span aria-hidden style={{ flexShrink: 0, opacity: 0.6, fontSize: 10 }}>▾</span>
      </button>
      {open && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 498, background: isMobile ? 'rgba(0,0,0,0.35)' : 'transparent' }} />
          <div
            role="listbox"
            style={isMobile
              ? { ...panelBase, position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '72vh', borderRadius: '18px 18px 0 0', paddingBottom: 'env(safe-area-inset-bottom)' }
              : { ...panelBase, position: 'fixed', top: pos?.top, bottom: pos?.bottom, left: pos?.left, width: pos?.width, maxHeight: pos?.maxHeight || 340, borderRadius: 12 }}
          >
            {panelBody}
          </div>
        </>, document.body)}
    </>
  );
}

const trigger = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '9px 11px', border: '1.5px solid var(--hairline)', borderRadius: 9, fontSize: 14, background: 'var(--card)', textAlign: 'left', fontFamily: 'inherit' };
const panelBase = { zIndex: 499, display: 'flex', flexDirection: 'column', background: 'var(--card)', border: '1px solid var(--hairline)', boxShadow: '0 10px 34px rgba(0,0,0,0.22)', overflow: 'hidden' };
const searchInput = { width: '100%', boxSizing: 'border-box', padding: '12px 14px', border: 'none', borderBottom: '1px solid var(--hairline)', fontSize: 15, outline: 'none', background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', flexShrink: 0 };
// Branch and leaf rows are ≥40px so they stay thumb-sized on a phone.
const branchBtn = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '8px 12px', border: 'none', background: 'transparent', color: 'var(--text)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: 13.5 };
const leafBtn = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '8px 12px 8px 42px', border: 'none', color: 'var(--text)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: 13.5 };
const caret = { fontSize: 9, color: 'var(--muted)', flexShrink: 0, width: 10, transition: 'transform .15s' };
const countNote = { marginLeft: 'auto', fontSize: 11, color: 'var(--muted)', flexShrink: 0 };
const pathNote = { fontSize: 11, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' };
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
const muted = { padding: '14px 14px', fontSize: 13, color: 'var(--muted)' };
